export interface HealthStatus {
  status: 'healthy' | 'degraded' | 'offline';
  latency?: number;
  message?: string;
  lastChecked: number;
}

// Cache key for API health status
const API_HEALTH_CACHE_KEY = 'api_health_status';

/**
 * Checks if the backend API is reachable and functioning
 */
export const checkApiHealth = async (): Promise<HealthStatus> => {
  try {
    const startTime = Date.now();
    
    // Add a random parameter to avoid cached responses
    const cacheBuster = `cb=${Date.now()}`;
    const url = `${import.meta.env.VITE_API_BASE_URL || ''}/api/health?${cacheBuster}`;
    
    // Check if the URL is valid before making the request
    if (!url.startsWith('http')) {
      console.warn('Invalid API URL, assuming API is offline');
      const status: HealthStatus = {
        status: 'offline',
        message: 'API URL not configured properly',
        lastChecked: Date.now(),
      };
      localStorage.setItem(API_HEALTH_CACHE_KEY, JSON.stringify(status));
      return status;
    }
    
    // Use AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      const latency = Date.now() - startTime;
      
      if (!response.ok) {
        const status: HealthStatus = {
          status: 'degraded',
          latency,
          message: `API returned status code ${response.status}`,
          lastChecked: Date.now(),
        };
        localStorage.setItem(API_HEALTH_CACHE_KEY, JSON.stringify(status));
        return status;
      }
      
      // Check content type to handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Handle non-JSON responses as degraded service
        const status: HealthStatus = {
          status: 'degraded',
          latency,
          message: 'API responded with non-JSON content',
          lastChecked: Date.now(),
        };
        localStorage.setItem(API_HEALTH_CACHE_KEY, JSON.stringify(status));
        return status;
      }
      
      // Parse JSON response
      const health = await response.json();
      
      const status: HealthStatus = {
        status: 'healthy',
        latency,
        message: health.message || 'API is healthy',
        lastChecked: Date.now(),
      };
      
      localStorage.setItem(API_HEALTH_CACHE_KEY, JSON.stringify(status));
      return status;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      // Handle abort errors separately
      if (fetchError.name === 'AbortError') {
        console.warn('API health check timed out');
        const status: HealthStatus = {
          status: 'degraded',
          message: 'API health check timed out',
          lastChecked: Date.now(),
        };
        localStorage.setItem(API_HEALTH_CACHE_KEY, JSON.stringify(status));
        return status;
      }
      
      throw fetchError;
    }
  } catch (error) {
    console.error('API health check failed:', error);
    
    const status: HealthStatus = {
      status: 'offline',
      message: error instanceof Error ? error.message : 'Unknown error',
      lastChecked: Date.now(),
    };
    
    localStorage.setItem(API_HEALTH_CACHE_KEY, JSON.stringify(status));
    return status;
  }
};

/**
 * Gets the cached API health status
 */
export const getCachedApiHealth = (): HealthStatus | null => {
  const cachedStatus = localStorage.getItem(API_HEALTH_CACHE_KEY);
  if (!cachedStatus) return null;
  
  try {
    return JSON.parse(cachedStatus);
  } catch (e) {
    return null;
  }
};